// Strings for release 4 (0020): who acts when a school isn't Live, the mood
// split in bands, trial vs demo school, safety-net review marks, student
// languages per school and per check-in, "You said, we did" over 30 days,
// the interim deputy route and the new notices. Merged into STRINGS in
// strings.ts. Finnish and Swedish are drafts and need native-speaker review.

type Shape<T> = { [K in keyof T]: T[K] extends (...a: infer A) => string ? (...a: A) => string : string };

export const r4En = {
  // K3: mood split
  'r4.band.low': 'Struggling or not great',
  'r4.band.mid': 'Okay',
  'r4.band.high': 'Good or great',
  'r4.bandsNote': 'Shown in three groups so that no single answer stands out.',
  'r4.moodHiddenTitle': 'Mood split hidden',
  'r4.moodHiddenBody': 'One answer would stand out, so only the average and the number of answers are shown.',

  // K1: readiness banner
  'r4.ready.title': 'Check-ins open once setup is finished',
  'r4.ready.missing': 'Still to do',
  'r4.ready.sandbox': (head: string | null) =>
    head ? `Zencera is finishing setup with your school. Questions: ${head}.` : 'Zencera is finishing setup with your school. Questions: your head of school.',
  'r4.ready.customer': (who: string) => `${who} need to complete setup.`,
  'r4.ready.customerNobody': 'Your head of school needs to complete setup.',
  'r4.ready.startHint': 'Start check-in is available once setup is finished.',
  'r4.ready.missingLangs': (langs: string) => `missing: ${langs}`,
  'r4.owner': (who: string) => `Who: ${who}`,
  'r4.owner.zencera': 'Zencera',
  'r4.owner.school_admin': 'School admin',
  'r4.owner.head': 'Head of school',
  'r4.lang.fi': 'Finnish',
  'r4.lang.sv': 'Swedish',
  'r4.lang.en': 'English',
  'r4.head.teachersBlocked': 'Teachers can’t start check-ins until the required items are done.',
  'r4.head.statusSandbox': 'Read-only. During the trial, Zencera completes these setup steps with your school.',

  // Checklist items added in 0020
  'r4.ck.safety_net_reviewed': 'Safety-net message checked by a native speaker',
  'r4.ck.languages_reviewed': 'Student-screen translations checked by a native speaker',
  'r4.ck.unreviewed': (langs: string) => `Not yet checked: ${langs}`,
  'r4.ck.draft': (langs: string) => `Draft: ${langs}`,

  // K2: trial vs demo school, paperwork note, DPO name
  'r4.demo.title': 'Trial or demo school',
  'r4.demo.isDemo': 'This is the demo school. No real students ever check in here, so the notice and the DPO review are warnings.',
  'r4.demo.isTrial': 'This is a trial school. The signed trial terms, the DPO review and the notice to students and guardians are required before check-ins.',
  'r4.demo.makeDemo': 'Make this the demo school',
  'r4.demo.makeTrial': 'Make this a trial school',
  'r4.demo.reason': 'Reason (logged)',
  'r4.demo.saved': 'Saved',
  'r4.demo.pill': 'Demo school',
  'r4.paperNote': 'Note (optional)',
  'r4.paperNoteHint': 'For example “TEST entry” for a test record.',
  'r4.dpoName': 'DPO’s name',
  'r4.dpoNameHint': 'Recorded with today’s date.',
  'r4.dpoNeedsName': 'Enter the DPO’s name first.',
  'r4.noteShown': (note: string) => `Note: ${note}`,

  // K4: safety-net review marks and the number check
  'r4.sn.reviewed': 'Checked by a native speaker',
  'r4.sn.notReviewed': 'Not yet checked by a native speaker',
  'r4.sn.markReviewed': 'Mark as checked',
  'r4.sn.unmark': 'Remove the mark',
  'r4.sn.mismatchTitle': 'Check these details',
  'r4.sn.mismatch': (items: string) => `They don’t appear in every language: ${items}. A wrong phone number or address could send a student to the wrong place.`,
  'r4.sn.reviewAfterSave': 'Save the text first; a changed text needs checking again.',

  // K5: languages (platform admin)
  'r4.langs.title': 'Student languages',
  'r4.langs.hint': 'Finnish, Swedish and English are always on. Add the home languages your students need. Languages never block the school from going Live.',
  'r4.langs.provisional': 'Provisional list. It will be replaced with Statistics Finland’s table of languages spoken in Finland.',
  'r4.langs.search': 'Find a language',
  'r4.langs.chosen': 'Offered at this school',
  'r4.langs.none': 'No extra languages yet.',
  'r4.langs.add': (name: string) => `Add ${name}`,
  'r4.langs.remove': (name: string) => `Remove ${name}`,
  'r4.langs.st.reviewed': 'Student screen checked',
  'r4.langs.st.draft': 'Draft, not yet checked by a native speaker',
  'r4.langs.st.none': 'Not translated: students see the screen in English',
  'r4.langs.netIn': (name: string) => `Safety-net message in ${name} (optional)`,
  'r4.langs.netFallback': 'Without a message in a language, students see the Finnish and English messages.',
  'r4.langs.save': 'Save languages',
  'r4.langs.noMatch': 'No language matches.',

  // K5: languages per check-in (teacher)
  'r4.tl.title': 'Languages',
  'r4.tl.hint': 'Finnish, Swedish and English are always on. Switch off the languages this class doesn’t need. Your choice is remembered for this class.',
  'r4.tl.always': 'Always on',
  'r4.tl.draft': 'draft',
  'r4.tl.english': 'shown in English',

  // B11, B12
  'r4.fcHowNoHistory': 'Based on the class size only: you haven’t run a check-in here yet.',
  'r4.noOpen': 'No check-in open',
  'r4.noTeacher': (classes: string) => `No teacher yet: ${classes}. Teachers can only start check-ins for classes they’re assigned to.`,
  'r4.notOpenYet': 'Check-ins haven’t started at your school yet',

  // K6: "You said, we did" over 30 days
  'r4.ys.added': 'Actions added since',
  'r4.ys.add': 'Add an action',
  'r4.ys.addHint': 'Students see your reply and these actions, newest first, until the reply is 30 days old.',
  'r4.ys.addButton': 'Add to the reply',
  'r4.ys.visibleUntil': (d: string) => `Students see this until ${d}.`,
  'r4.ys.addedOk': 'Action added',
  'r4.ci.updated': (d: string) => `Updated ${d}`,
  'r4.ci.notTranslated': 'This screen isn’t translated into this language yet, so it’s shown in English.',

  // B9: interim deputy route
  'r4.head.noDeputies': 'No deputy heads yet. Until your school admin has their own screens, Zencera support adds deputies for you.',
  'r4.head.askSupport': 'Ask Zencera support to add a deputy',
  'r4.head.askNote': 'Who should be the deputy? (optional)',
  'r4.head.askSend': 'Send the request',
  'r4.head.asked': 'Zencera support has been asked. They’ll be in touch.',

  // New notices
  'r4.n.language_removed': (langs: string) => `Your school no longer offers ${langs}. It has been removed from your class’s languages.`,
  'r4.n.deputy_help_requested': (who: string) => `${who} (head of school) asks Zencera to add a deputy head.`,
  'r4.n.approval_reminder': (days: number) =>
    days === 1 ? 'A role request is waiting for your decision. 1 day left before it expires.' : `A role request is waiting for your decision. ${days} days left before it expires.`,
  'r4.n.test_account_expiring': (days: number) => `Your test account ends in about ${days} days. Ask Zencera to extend it if you still need it.`,
  'r4.n.test_account_expiring_admin': (name: string, days: number) => `${name}: test account ends in about ${days} days.`,
  'r4.n.test_expiry_blocked': (name: string) => `${name}’s test account has expired but was kept: the school would lose its last school admin or its primary head. Extend it or appoint someone else.`,

  // B8: Activity labels
  'r4.act.role_added': 'Role added',
  'r4.act.role_changed': 'Role changed',
  'r4.act.role_removed': 'Role removed',
  'r4.act.insight_viewed': 'Viewed results',
  'r4.act.session_results': 'Viewed check-in results',
  'r4.act.school_results': 'Viewed school results',
  'r4.act.signed_in': 'Signed in',
  'r4.act.student_languages_changed': 'Student languages changed',
  'r4.act.safety_net_reviewed': 'Safety-net message marked as checked',
  'r4.act.safety_net_review_removed': 'Safety-net check mark removed',
  'r4.act.demo_school_changed': 'Trial or demo school changed',
  'r4.act.deputy_help_requested': 'Asked Zencera for a deputy head',
  'r4.act.setup_step_done': 'Setup step done',
  'r4.act.setup_step_reopened': 'Setup step reopened',
  'r4.act.school_ready': 'School is Live',
  'r4.act.class_added': 'Class added',
  'r4.act.class_changed': 'Class changed',
  'r4.act.assignment_added': 'Class assignment added',
  'r4.act.assignment_ended': 'Class assignment ended',
  'r4.act.reply_posted': '“You said, we did” posted',
  'r4.act.reply_changed': '“You said, we did” changed',
  'r4.act.role_activated': 'Signed in with a role',
  'r4.act.role_switched': 'Switched role',
  'r4.act.approval_decided': 'Role request decided',
  'r4.act.deputy_scope_set': 'Deputy’s grades changed',
  'r4.act.approval_delegate_set': 'Approval delegate named',
  'r4.act.approval_delegate_ended': 'Approval delegate ended',
  'r4.act.primary_head_handed_over': 'Primary head handed over',
  'r4.act.primary_head_appointed': 'Primary head appointed',
  'r4.act.person_deactivated': 'Person deactivated',
  'r4.act.person_reactivated': 'Person reactivated',
  'r4.act.person_erased': 'Person erased',
  'r4.act.test_account_created': 'Test account created',
  'r4.act.test_account_extended': 'Test account extended',
  'r4.act.test_password_set': 'Own password chosen',
  'r4.act.support_mode_entered': 'Zencera support started helping',
  'r4.act.support_mode_left': 'Zencera support finished',
  'r4.act.staff_notice_acknowledged': 'Staff notice read',
  'r4.act.council_link_created': 'Student council link created',
  'r4.act.council_link_revoked': 'Student council link withdrawn',
  'r4.act.welfare_report_exported': 'Welfare report exported',
  'r4.act.entitlement_changed': 'Premium option changed',
  'r4.act.document_accepted': 'Document accepted',
  'r4.act.invite_queued': 'Invite queued',
  'r4.act.invite_cancelled': 'Invite cancelled',
  'r4.act.school_settings_changed': 'School settings changed',
  'r4.act.class_deleted': 'Class deleted',
  'r4.act.assignment_changed': 'Class assignment changed',
  'r4.act.cross_school_changed': 'Cross-school sharing changed',
  'r4.act.other': 'Change recorded',
  'r4.who.support': 'Zencera support',
  'r4.who.system': 'Swipe (automatic)',
  'r4.who.former': 'Former staff member',
  // Errors raised by 0020
  'err.reply_expired': 'This reply is older than 30 days. Post a new one on a recent check-in.',
  'err.too_many_items': 'A reply can list up to 8 more actions.',
  'err.sandbox_only': 'Only a Sandbox school can be the demo school.',
  'err.no_text': 'Write the message in this language first.',
  'err.help_requested_today': 'Zencera support was asked today. They’ll be in touch.',
};

export type R4Strings = Shape<typeof r4En>;

export const r4Fi: R4Strings = {
  'r4.band.low': 'Vaikeaa tai ei kovin hyvin',
  'r4.band.mid': 'Ihan ok',
  'r4.band.high': 'Hyvin tai tosi hyvin',
  'r4.bandsNote': 'Näytetään kolmena ryhmänä, jotta yksittäinen vastaus ei erotu.',
  'r4.moodHiddenTitle': 'Mielialajakauma piilotettu',
  'r4.moodHiddenBody': 'Yksi vastaus erottuisi, joten näytetään vain keskiarvo ja vastausten määrä.',

  'r4.ready.title': 'Kyselyt avautuvat, kun käyttöönotto on valmis',
  'r4.ready.missing': 'Vielä tekemättä',
  'r4.ready.sandbox': (head: string | null) =>
    head ? `Zencera viimeistelee käyttöönottoa koulusi kanssa. Kysymykset: ${head}.` : 'Zencera viimeistelee käyttöönottoa koulusi kanssa. Kysymykset: rehtorisi.',
  'r4.ready.customer': (who: string) => `${who} viimeistelee käyttöönoton.`,
  'r4.ready.customerNobody': 'Rehtorisi viimeistelee käyttöönoton.',
  'r4.ready.startHint': 'Kyselyn voi aloittaa, kun käyttöönotto on valmis.',
  'r4.ready.missingLangs': (langs: string) => `puuttuu: ${langs}`,
  'r4.owner': (who: string) => `Kuka: ${who}`,
  'r4.owner.zencera': 'Zencera',
  'r4.owner.school_admin': 'Koulun ylläpitäjä',
  'r4.owner.head': 'Rehtori',
  'r4.lang.fi': 'suomi',
  'r4.lang.sv': 'ruotsi',
  'r4.lang.en': 'englanti',
  'r4.head.teachersBlocked': 'Opettajat voivat aloittaa kyselyjä vasta, kun pakolliset kohdat on tehty.',
  'r4.head.statusSandbox': 'Vain luku. Kokeilun aikana Zencera tekee nämä käyttöönoton vaiheet koulusi kanssa.',

  'r4.ck.safety_net_reviewed': 'Äidinkielinen on tarkistanut tukiviestin',
  'r4.ck.languages_reviewed': 'Äidinkielinen on tarkistanut oppilasnäkymän käännökset',
  'r4.ck.unreviewed': (langs: string) => `Tarkistamatta: ${langs}`,
  'r4.ck.draft': (langs: string) => `Luonnos: ${langs}`,

  'r4.demo.title': 'Kokeilu- vai esittelykoulu',
  'r4.demo.isDemo': 'Tämä on esittelykoulu. Täällä ei koskaan vastaa oikeita oppilaita, joten tiedote ja tietosuojavastaavan tarkistus ovat varoituksia.',
  'r4.demo.isTrial': 'Tämä on kokeilukoulu. Allekirjoitetut kokeiluehdot, tietosuojavastaavan tarkistus ja tiedote oppilaille ja huoltajille vaaditaan ennen kyselyjä.',
  'r4.demo.makeDemo': 'Tee tästä esittelykoulu',
  'r4.demo.makeTrial': 'Tee tästä kokeilukoulu',
  'r4.demo.reason': 'Syy (kirjataan lokiin)',
  'r4.demo.saved': 'Tallennettu',
  'r4.demo.pill': 'Esittelykoulu',
  'r4.paperNote': 'Huomautus (vapaaehtoinen)',
  'r4.paperNoteHint': 'Esimerkiksi ”TESTI” testikirjaukselle.',
  'r4.dpoName': 'Tietosuojavastaavan nimi',
  'r4.dpoNameHint': 'Kirjataan tämän päivän päivämäärällä.',
  'r4.dpoNeedsName': 'Kirjoita ensin tietosuojavastaavan nimi.',
  'r4.noteShown': (note: string) => `Huomautus: ${note}`,

  'r4.sn.reviewed': 'Äidinkielinen tarkistanut',
  'r4.sn.notReviewed': 'Äidinkielinen ei ole vielä tarkistanut',
  'r4.sn.markReviewed': 'Merkitse tarkistetuksi',
  'r4.sn.unmark': 'Poista merkintä',
  'r4.sn.mismatchTitle': 'Tarkista nämä tiedot',
  'r4.sn.mismatch': (items: string) => `Ne eivät ole mukana kaikissa kielissä: ${items}. Väärä puhelinnumero tai osoite voisi ohjata oppilaan väärään paikkaan.`,
  'r4.sn.reviewAfterSave': 'Tallenna teksti ensin; muutettu teksti on tarkistettava uudelleen.',

  'r4.langs.title': 'Oppilaiden kielet',
  'r4.langs.hint': 'Suomi, ruotsi ja englanti ovat aina käytössä. Lisää oppilaiden tarvitsemat kotikielet. Kielet eivät koskaan estä koulun käyttöönottoa.',
  'r4.langs.provisional': 'Alustava luettelo. Se korvataan Tilastokeskuksen taulukolla Suomessa puhutuista kielistä.',
  'r4.langs.search': 'Etsi kieli',
  'r4.langs.chosen': 'Koulussa käytössä',
  'r4.langs.none': 'Ei vielä lisäkieliä.',
  'r4.langs.add': (name: string) => `Lisää ${name}`,
  'r4.langs.remove': (name: string) => `Poista ${name}`,
  'r4.langs.st.reviewed': 'Oppilasnäkymä tarkistettu',
  'r4.langs.st.draft': 'Luonnos, äidinkielinen ei ole vielä tarkistanut',
  'r4.langs.st.none': 'Ei käännetty: oppilaat näkevät näkymän englanniksi',
  'r4.langs.netIn': (name: string) => `Tukiviesti kielellä ${name} (vapaaehtoinen)`,
  'r4.langs.netFallback': 'Jos viestiä ei ole jollakin kielellä, oppilaat näkevät suomen- ja englanninkieliset viestit.',
  'r4.langs.save': 'Tallenna kielet',
  'r4.langs.noMatch': 'Yksikään kieli ei vastaa hakua.',

  'r4.tl.title': 'Kielet',
  'r4.tl.hint': 'Suomi, ruotsi ja englanti ovat aina käytössä. Poista kielet, joita luokka ei tarvitse. Valinta muistetaan tälle luokalle.',
  'r4.tl.always': 'Aina käytössä',
  'r4.tl.draft': 'luonnos',
  'r4.tl.english': 'näytetään englanniksi',

  'r4.fcHowNoHistory': 'Perustuu vain luokan kokoon: et ole vielä pitänyt kyselyä tässä luokassa.',
  'r4.noOpen': 'Ei avointa kyselyä',
  'r4.noTeacher': (classes: string) => `Ei vielä opettajaa: ${classes}. Opettajat voivat aloittaa kyselyjä vain omille luokilleen.`,
  'r4.notOpenYet': 'Koulussasi ei ole vielä aloitettu kyselyjä',

  'r4.ys.added': 'Lisätyt toimet',
  'r4.ys.add': 'Lisää toimi',
  'r4.ys.addHint': 'Oppilaat näkevät vastauksesi ja nämä toimet uusin ensin, kunnes vastaus on 30 päivää vanha.',
  'r4.ys.addButton': 'Lisää vastaukseen',
  'r4.ys.visibleUntil': (d: string) => `Oppilaat näkevät tämän ${d} asti.`,
  'r4.ys.addedOk': 'Toimi lisätty',
  'r4.ci.updated': (d: string) => `Päivitetty ${d}`,
  'r4.ci.notTranslated': 'Tätä näkymää ei ole vielä käännetty tälle kielelle, joten se näytetään englanniksi.',

  'r4.head.noDeputies': 'Ei vielä apulaisrehtoreita. Kunnes koulun ylläpitäjällä on omat näkymänsä, Zencera-tuki lisää apulaisrehtorit puolestasi.',
  'r4.head.askSupport': 'Pyydä Zencera-tukea lisäämään apulaisrehtori',
  'r4.head.askNote': 'Kuka olisi apulaisrehtori? (vapaaehtoinen)',
  'r4.head.askSend': 'Lähetä pyyntö',
  'r4.head.asked': 'Zencera-tukea on pyydetty. He ottavat yhteyttä.',

  'r4.n.language_removed': (langs: string) => `Koulusi ei enää tarjoa kieltä ${langs}. Se on poistettu luokkasi kielistä.`,
  'r4.n.deputy_help_requested': (who: string) => `${who} (rehtori) pyytää Zenceraa lisäämään apulaisrehtorin.`,
  'r4.n.approval_reminder': (days: number) =>
    days === 1 ? 'Roolipyyntö odottaa päätöstäsi. 1 päivä ennen kuin se vanhenee.' : `Roolipyyntö odottaa päätöstäsi. ${days} päivää ennen kuin se vanhenee.`,
  'r4.n.test_account_expiring': (days: number) => `Testitunnuksesi päättyy noin ${days} päivän kuluttua. Pyydä Zenceraa jatkamaan sitä, jos tarvitset sitä vielä.`,
  'r4.n.test_account_expiring_admin': (name: string, days: number) => `${name}: testitunnus päättyy noin ${days} päivän kuluttua.`,
  'r4.n.test_expiry_blocked': (name: string) => `Käyttäjän ${name} testitunnus on vanhentunut, mutta sitä ei poistettu: koulu menettäisi viimeisen ylläpitäjänsä tai päärehtorinsa. Jatka tunnusta tai nimeä joku toinen.`,

  'r4.act.role_added': 'Rooli lisätty',
  'r4.act.role_changed': 'Roolia muutettu',
  'r4.act.role_removed': 'Rooli poistettu',
  'r4.act.insight_viewed': 'Katsoi tuloksia',
  'r4.act.session_results': 'Katsoi kyselyn tuloksia',
  'r4.act.school_results': 'Katsoi koulun tuloksia',
  'r4.act.signed_in': 'Kirjautui sisään',
  'r4.act.student_languages_changed': 'Oppilaiden kieliä muutettu',
  'r4.act.safety_net_reviewed': 'Tukiviesti merkitty tarkistetuksi',
  'r4.act.safety_net_review_removed': 'Tukiviestin tarkistusmerkintä poistettu',
  'r4.act.demo_school_changed': 'Kokeilu- tai esittelykoulu muutettu',
  'r4.act.deputy_help_requested': 'Pyysi Zenceralta apulaisrehtoria',
  'r4.act.setup_step_done': 'Käyttöönoton vaihe tehty',
  'r4.act.setup_step_reopened': 'Käyttöönoton vaihe avattu uudelleen',
  'r4.act.school_ready': 'Koulu on käytössä',
  'r4.act.class_added': 'Luokka lisätty',
  'r4.act.class_changed': 'Luokkaa muutettu',
  'r4.act.assignment_added': 'Luokka-asetus lisätty',
  'r4.act.assignment_ended': 'Luokka-asetus päättyi',
  'r4.act.reply_posted': '”Te sanoitte, me teimme” julkaistu',
  'r4.act.reply_changed': '”Te sanoitte, me teimme” muutettu',
  'r4.act.role_activated': 'Kirjautui roolilla',
  'r4.act.role_switched': 'Vaihtoi roolia',
  'r4.act.approval_decided': 'Roolipyyntö ratkaistu',
  'r4.act.deputy_scope_set': 'Apulaisrehtorin luokka-asteita muutettu',
  'r4.act.approval_delegate_set': 'Hyväksyjän sijainen nimetty',
  'r4.act.approval_delegate_ended': 'Hyväksyjän sijaisuus päättyi',
  'r4.act.primary_head_handed_over': 'Päärehtorin tehtävä siirretty',
  'r4.act.primary_head_appointed': 'Päärehtori nimetty',
  'r4.act.person_deactivated': 'Henkilö poistettu käytöstä',
  'r4.act.person_reactivated': 'Henkilö palautettu käyttöön',
  'r4.act.person_erased': 'Henkilön tiedot poistettu',
  'r4.act.test_account_created': 'Testitunnus luotu',
  'r4.act.test_account_extended': 'Testitunnusta jatkettu',
  'r4.act.test_password_set': 'Oma salasana valittu',
  'r4.act.support_mode_entered': 'Zencera-tuki aloitti avustamisen',
  'r4.act.support_mode_left': 'Zencera-tuki lopetti avustamisen',
  'r4.act.staff_notice_acknowledged': 'Henkilöstötiedote luettu',
  'r4.act.council_link_created': 'Oppilaskunnan linkki luotu',
  'r4.act.council_link_revoked': 'Oppilaskunnan linkki peruttu',
  'r4.act.welfare_report_exported': 'Hyvinvointiraportti viety',
  'r4.act.entitlement_changed': 'Lisäpalvelua muutettu',
  'r4.act.document_accepted': 'Asiakirja hyväksytty',
  'r4.act.invite_queued': 'Kutsu jonossa',
  'r4.act.invite_cancelled': 'Kutsu peruttu',
  'r4.act.school_settings_changed': 'Koulun asetuksia muutettu',
  'r4.act.class_deleted': 'Luokka poistettu',
  'r4.act.assignment_changed': 'Luokka-asetusta muutettu',
  'r4.act.cross_school_changed': 'Koulujen välistä jakamista muutettu',
  'r4.act.other': 'Muutos kirjattu',
  'r4.who.support': 'Zencera-tuki',
  'r4.who.system': 'Swipe (automaattinen)',
  'r4.who.former': 'Entinen työntekijä',
  'err.reply_expired': 'Tämä vastaus on yli 30 päivää vanha. Julkaise uusi viimeaikaisesta kyselystä.',
  'err.too_many_items': 'Vastaukseen voi lisätä enintään 8 toimea.',
  'err.sandbox_only': 'Vain Sandbox-koulu voi olla esittelykoulu.',
  'err.no_text': 'Kirjoita ensin viesti tällä kielellä.',
  'err.help_requested_today': 'Zencera-tukea on jo pyydetty tänään. He ottavat yhteyttä.',
};

export const r4Sv: R4Strings = {
  'r4.band.low': 'Jobbigt eller inte så bra',
  'r4.band.mid': 'Okej',
  'r4.band.high': 'Bra eller jättebra',
  'r4.bandsNote': 'Visas i tre grupper så att inget enskilt svar sticker ut.',
  'r4.moodHiddenTitle': 'Fördelningen av humör dold',
  'r4.moodHiddenBody': 'Ett svar skulle sticka ut, så bara medelvärdet och antalet svar visas.',

  'r4.ready.title': 'Enkäterna öppnar när introduktionen är klar',
  'r4.ready.missing': 'Återstår',
  'r4.ready.sandbox': (head: string | null) =>
    head ? `Zencera slutför introduktionen med din skola. Frågor: ${head}.` : 'Zencera slutför introduktionen med din skola. Frågor: din rektor.',
  'r4.ready.customer': (who: string) => `${who} slutför introduktionen.`,
  'r4.ready.customerNobody': 'Din rektor slutför introduktionen.',
  'r4.ready.startHint': 'Du kan starta en enkät när introduktionen är klar.',
  'r4.ready.missingLangs': (langs: string) => `saknas: ${langs}`,
  'r4.owner': (who: string) => `Vem: ${who}`,
  'r4.owner.zencera': 'Zencera',
  'r4.owner.school_admin': 'Skolans administratör',
  'r4.owner.head': 'Rektor',
  'r4.lang.fi': 'finska',
  'r4.lang.sv': 'svenska',
  'r4.lang.en': 'engelska',
  'r4.head.teachersBlocked': 'Lärarna kan inte starta enkäter förrän de obligatoriska punkterna är klara.',
  'r4.head.statusSandbox': 'Endast läsning. Under försöket gör Zencera de här introduktionsstegen tillsammans med din skola.',

  'r4.ck.safety_net_reviewed': 'Stödmeddelandet granskat av en modersmålstalare',
  'r4.ck.languages_reviewed': 'Elevvyns översättningar granskade av en modersmålstalare',
  'r4.ck.unreviewed': (langs: string) => `Inte granskat: ${langs}`,
  'r4.ck.draft': (langs: string) => `Utkast: ${langs}`,

  'r4.demo.title': 'Försöks- eller demoskola',
  'r4.demo.isDemo': 'Det här är demoskolan. Inga riktiga elever svarar här, så meddelandet och dataskyddsombudets granskning är varningar.',
  'r4.demo.isTrial': 'Det här är en försöksskola. De undertecknade försöksvillkoren, dataskyddsombudets granskning och meddelandet till elever och vårdnadshavare krävs före enkäterna.',
  'r4.demo.makeDemo': 'Gör detta till demoskolan',
  'r4.demo.makeTrial': 'Gör detta till en försöksskola',
  'r4.demo.reason': 'Orsak (loggas)',
  'r4.demo.saved': 'Sparat',
  'r4.demo.pill': 'Demoskola',
  'r4.paperNote': 'Anteckning (frivillig)',
  'r4.paperNoteHint': 'Till exempel ”TEST” för en testpost.',
  'r4.dpoName': 'Dataskyddsombudets namn',
  'r4.dpoNameHint': 'Registreras med dagens datum.',
  'r4.dpoNeedsName': 'Skriv först dataskyddsombudets namn.',
  'r4.noteShown': (note: string) => `Anteckning: ${note}`,

  'r4.sn.reviewed': 'Granskat av en modersmålstalare',
  'r4.sn.notReviewed': 'Inte granskat av en modersmålstalare ännu',
  'r4.sn.markReviewed': 'Markera som granskat',
  'r4.sn.unmark': 'Ta bort markeringen',
  'r4.sn.mismatchTitle': 'Kontrollera de här uppgifterna',
  'r4.sn.mismatch': (items: string) => `De finns inte på alla språk: ${items}. Ett fel telefonnummer eller en fel adress kan skicka en elev fel.`,
  'r4.sn.reviewAfterSave': 'Spara texten först; en ändrad text måste granskas igen.',

  'r4.langs.title': 'Elevernas språk',
  'r4.langs.hint': 'Finska, svenska och engelska är alltid på. Lägg till de hemspråk eleverna behöver. Språk hindrar aldrig skolan från att tas i bruk.',
  'r4.langs.provisional': 'Preliminär lista. Den ersätts med Statistikcentralens tabell över språk som talas i Finland.',
  'r4.langs.search': 'Sök ett språk',
  'r4.langs.chosen': 'Används i skolan',
  'r4.langs.none': 'Inga extra språk ännu.',
  'r4.langs.add': (name: string) => `Lägg till ${name}`,
  'r4.langs.remove': (name: string) => `Ta bort ${name}`,
  'r4.langs.st.reviewed': 'Elevvyn granskad',
  'r4.langs.st.draft': 'Utkast, inte granskat av en modersmålstalare',
  'r4.langs.st.none': 'Inte översatt: eleverna ser vyn på engelska',
  'r4.langs.netIn': (name: string) => `Stödmeddelande på ${name} (frivilligt)`,
  'r4.langs.netFallback': 'Utan meddelande på ett språk ser eleverna meddelandena på finska och engelska.',
  'r4.langs.save': 'Spara språken',
  'r4.langs.noMatch': 'Inget språk matchar.',

  'r4.tl.title': 'Språk',
  'r4.tl.hint': 'Finska, svenska och engelska är alltid på. Stäng av språk som klassen inte behöver. Valet sparas för klassen.',
  'r4.tl.always': 'Alltid på',
  'r4.tl.draft': 'utkast',
  'r4.tl.english': 'visas på engelska',

  'r4.fcHowNoHistory': 'Bygger bara på klassens storlek: du har inte haft någon enkät i klassen ännu.',
  'r4.noOpen': 'Ingen öppen enkät',
  'r4.noTeacher': (classes: string) => `Ingen lärare ännu: ${classes}. Lärare kan bara starta enkäter för sina egna klasser.`,
  'r4.notOpenYet': 'Enkäterna har inte börjat i din skola ännu',

  'r4.ys.added': 'Tillagda åtgärder',
  'r4.ys.add': 'Lägg till en åtgärd',
  'r4.ys.addHint': 'Eleverna ser ditt svar och åtgärderna, den nyaste först, tills svaret är 30 dagar gammalt.',
  'r4.ys.addButton': 'Lägg till i svaret',
  'r4.ys.visibleUntil': (d: string) => `Eleverna ser detta till och med ${d}.`,
  'r4.ys.addedOk': 'Åtgärden tillagd',
  'r4.ci.updated': (d: string) => `Uppdaterat ${d}`,
  'r4.ci.notTranslated': 'Vyn är inte översatt till det här språket ännu, så den visas på engelska.',

  'r4.head.noDeputies': 'Inga biträdande rektorer ännu. Tills skolans administratör har egna vyer lägger Zenceras support till biträdande rektorer åt dig.',
  'r4.head.askSupport': 'Be Zenceras support lägga till en biträdande rektor',
  'r4.head.askNote': 'Vem ska bli biträdande rektor? (frivilligt)',
  'r4.head.askSend': 'Skicka begäran',
  'r4.head.asked': 'Zenceras support har kontaktats. De hör av sig.',

  'r4.n.language_removed': (langs: string) => `Skolan erbjuder inte längre ${langs}. Det har tagits bort från klassens språk.`,
  'r4.n.deputy_help_requested': (who: string) => `${who} (rektor) ber Zencera lägga till en biträdande rektor.`,
  'r4.n.approval_reminder': (days: number) =>
    days === 1 ? 'En rollbegäran väntar på ditt beslut. 1 dag kvar innan den går ut.' : `En rollbegäran väntar på ditt beslut. ${days} dagar kvar innan den går ut.`,
  'r4.n.test_account_expiring': (days: number) => `Ditt testkonto upphör om cirka ${days} dagar. Be Zencera förlänga det om du fortfarande behöver det.`,
  'r4.n.test_account_expiring_admin': (name: string, days: number) => `${name}: testkontot upphör om cirka ${days} dagar.`,
  'r4.n.test_expiry_blocked': (name: string) => `${name}s testkonto har gått ut men sparades: skolan skulle förlora sin sista administratör eller sin huvudrektor. Förläng kontot eller utse någon annan.`,

  'r4.act.role_added': 'Roll tillagd',
  'r4.act.role_changed': 'Roll ändrad',
  'r4.act.role_removed': 'Roll borttagen',
  'r4.act.insight_viewed': 'Tittade på resultat',
  'r4.act.session_results': 'Tittade på enkätresultat',
  'r4.act.school_results': 'Tittade på skolans resultat',
  'r4.act.signed_in': 'Loggade in',
  'r4.act.student_languages_changed': 'Elevernas språk ändrade',
  'r4.act.safety_net_reviewed': 'Stödmeddelandet markerat som granskat',
  'r4.act.safety_net_review_removed': 'Granskningsmarkeringen borttagen',
  'r4.act.demo_school_changed': 'Försöks- eller demoskola ändrad',
  'r4.act.deputy_help_requested': 'Bad Zencera om en biträdande rektor',
  'r4.act.setup_step_done': 'Introduktionssteg klart',
  'r4.act.setup_step_reopened': 'Introduktionssteg öppnat igen',
  'r4.act.school_ready': 'Skolan är i bruk',
  'r4.act.class_added': 'Klass tillagd',
  'r4.act.class_changed': 'Klass ändrad',
  'r4.act.assignment_added': 'Klassuppdrag tillagt',
  'r4.act.assignment_ended': 'Klassuppdrag avslutat',
  'r4.act.reply_posted': '”Ni sa, vi gjorde” publicerat',
  'r4.act.reply_changed': '”Ni sa, vi gjorde” ändrat',
  'r4.act.role_activated': 'Loggade in med en roll',
  'r4.act.role_switched': 'Bytte roll',
  'r4.act.approval_decided': 'Rollbegäran avgjord',
  'r4.act.deputy_scope_set': 'Biträdande rektorns årskurser ändrade',
  'r4.act.approval_delegate_set': 'Ersättare för godkännanden utsedd',
  'r4.act.approval_delegate_ended': 'Ersättare för godkännanden avslutad',
  'r4.act.primary_head_handed_over': 'Huvudrektorskapet överlämnat',
  'r4.act.primary_head_appointed': 'Huvudrektor utsedd',
  'r4.act.person_deactivated': 'Person inaktiverad',
  'r4.act.person_reactivated': 'Person återaktiverad',
  'r4.act.person_erased': 'Person raderad',
  'r4.act.test_account_created': 'Testkonto skapat',
  'r4.act.test_account_extended': 'Testkonto förlängt',
  'r4.act.test_password_set': 'Eget lösenord valt',
  'r4.act.support_mode_entered': 'Zenceras support började hjälpa',
  'r4.act.support_mode_left': 'Zenceras support avslutade',
  'r4.act.staff_notice_acknowledged': 'Personalmeddelandet läst',
  'r4.act.council_link_created': 'Elevrådets länk skapad',
  'r4.act.council_link_revoked': 'Elevrådets länk återkallad',
  'r4.act.welfare_report_exported': 'Välfärdsrapporten exporterad',
  'r4.act.entitlement_changed': 'Tilläggstjänst ändrad',
  'r4.act.document_accepted': 'Dokument godkänt',
  'r4.act.invite_queued': 'Inbjudan i kö',
  'r4.act.invite_cancelled': 'Inbjudan avbruten',
  'r4.act.school_settings_changed': 'Skolans inställningar ändrade',
  'r4.act.class_deleted': 'Klass borttagen',
  'r4.act.assignment_changed': 'Klassuppdrag ändrat',
  'r4.act.cross_school_changed': 'Delning mellan skolor ändrad',
  'r4.act.other': 'Ändring registrerad',
  'r4.who.support': 'Zenceras support',
  'r4.who.system': 'Swipe (automatiskt)',
  'r4.who.former': 'Tidigare anställd',
  'err.reply_expired': 'Svaret är äldre än 30 dagar. Publicera ett nytt på en ny enkät.',
  'err.too_many_items': 'Ett svar kan ha högst 8 tillagda åtgärder.',
  'err.sandbox_only': 'Bara en Sandbox-skola kan vara demoskolan.',
  'err.no_text': 'Skriv först meddelandet på det här språket.',
  'err.help_requested_today': 'Zenceras support har redan kontaktats i dag. De hör av sig.',
};
